import {
  Exclude,
  plainToClassFromExist,
  Transform,
  Type
} from 'class-transformer';

import {Socket} from '../../classes/socket';
import {Helper} from '../../helpers/helper';
import {LookinChatSDK} from '../../lib/lookinChatSDK';
import {User} from '../user';

import {MessageContent} from './messageContent';
import {MessageRequest} from './messageRequest';
import {
  MessageState,
  MessageStateCollection,
  MessageStateType
} from './messageState';

/**
 * Single Message entity<br>
 * Contain all method to handle this message.F
 *
 * @category Message Entity
 * */
export class Message {

  /**
   * Main id of message, can be null if was just created and wasn't created on server yet
   * */
  id: string = null;

  /**
   * Works as primary id while message wasn't saved on server.
   * */
  clientId: string;

  /**
   * Group ID of message
   * */
  groupId: string;

  /**
   * Message type<br>
   * `userMessage` - if message was sent by user<br>
   * `infoMessage` - if it is info ( system ) message
   * */
  type: MessageType = 'userMessage';

  /**
   * Message status<br>
   * @see MessageStatus
   * */
  status: MessageStatus = 'sending';

  /**
   * @deprecated permission moved to groups, will be removed from messages very soon
   * */
  permissions: string[] = [];

  /**
   * Message content
   * */
  @Type(() => MessageContent)
  content: MessageContent = null;

  /**
   * Message state collection<br>
   * Each state is somehow related to change of this message:<br>
   * - if it was read, by who and when<br>
   * - if it was edited, by who and when<br>
   * - if it was reacted, by who and when and which reaction<br>
   * so on...<br>
   * */
  @Type(() => MessageState)
  @Transform(({value}) => new MessageStateCollection(...value))
  state: MessageStateCollection = new MessageStateCollection();

  /**
   * Initial author of this message.<br>
   * can be null of this is purely `infoMessage`.<br>
   * for example, if message will be deleted, then it will be also `infoMessage` but with author.
   * */
  @Type(() => User)
  user: User = null;

  /**
   * Event that will be called, when message entity changed<br>
   * {@link registerOnUpdate}
   *
   * @event
   * @return array of change types {@link MessageStateType}
   * */
  @Exclude()
  private onUpdate: (type: MessageStateType[]) => void = null;

  /**
   * If message is ready, saved to database
   * */
  public get ready() {
    return this.status === 'sent';
  }

  /**
   * Only for internal needs
   *
   * @ignore
   * @internal
   * */
  constructor(content: MessageContent, state: MessageState, groupId: string, clientId: string = null) {
    this.content = content;
    this.state.push(state);
    this.user = LookinChatSDK.user;
    this.groupId = groupId;

    this.clientId = clientId === null ? Helper.getRandomClientId(this.groupId + LookinChatSDK.user.id) : clientId;
  }

  /**
   * Only for internal needs
   *
   * @ignore
   * @internal
   * */
  update(item: Message, type: MessageStateType[] = []) {
    if (this.content.attachments.length && item.content.attachments.length) {
      this.content.attachments = [];
    }

    Object.assign(this, plainToClassFromExist(this, item));

    this.callOnUpdate(type);
  }

  /**
   * Only for internal needs
   *
   * @ignore
   * @internal
   * */
  callOnUpdate(type: MessageStateType[] = []) {
    if (this.onUpdate) {
      this.onUpdate(type);
    }
  }

  /**
   *  Mark message as read by current user<br>
   *  New state `read` will be added immediately, and {@link onUpdate} will be called<br>
   *  After server will handle request, message will be updated again and {@link onUpdate} will be called again,<br>
   *  Also {@link Group.onMessagesUpdate} will be called<br>
   *  If message not ready, or if read will be called from author of message, or it was already reed by this user, changes will be not made<br>
   *
   * @return Promise, resolve if all ok ( or cant read ), throw error in case of error.
   * */
  read() {
    return new Promise<void>((resolve, reject) => {
      if (this.ready && this.user?.id !== LookinChatSDK.user.id) {
        const existedSate = this.state.getByUserAndType('read', LookinChatSDK.user)

        if (!existedSate) {
          const newState = new MessageState('read');
          const request = new MessageRequest(['read'], this.groupId, newState, null, this.id);

          this.state.push(newState);
          this.callOnUpdate(['read']);

          // request.groupId = '123';

          Socket.instance.messageSocket.sendRequest(request).then((item) => {
            // @todo think something better
            LookinChatSDK.instance.groups.getById(this.groupId)?.callOnMessagesUpdate([item]);

            this.update(item.message, item.type);

            resolve()
          }).catch((error) => {
            LookinChatSDK.instance.groups.getById(this.groupId)?.callOnMessagesUpdate(error.result.items);
            this.update(error.result.items[0].message, error.result.items[0].type);

            reject(error);
          });
        } else {
          resolve()
        }
      } else {
        resolve()
      }
    });
  }

  /**
   *  Add reaction to message<br>
   *  New state `reaction` will be added immediately, and {@link onUpdate} will be called<br>
   *  After server will handle request, group will be updated again and {@link onUpdate} will be called again,<br>
   *  Also {@link Group.onMessagesUpdate} will be called<br>
   *  If message not ready, changes will be not made<br>
   *  If existed reaction from this user will be sent, {@link deleteReaction} will be called instead<br>
   *  If user already reacted, and send another reaction, then it will be replaced with new one
   *
   *  @param reaction
   *
   * @return Promise, resolve if all ok ( or cant read ), throw error in case of error.
   * */
  addReaction(reaction: string) {
    return new Promise<void>((resolve, reject) => {
      if (this.ready) {
        // @todo check if reactions from available list

        const existedSate = this.state.getByUserAndType('reaction', LookinChatSDK.user)

        if (existedSate) {
          if (existedSate.content === reaction) {
            this.deleteReaction().then(() => {
              resolve
            }).catch((error) => {
              reject(error)
            });
            return;
          }

          this.state.removeByUserAndType('reaction', LookinChatSDK.user);
        }

        const newState = new MessageState('reaction', reaction);
        const request = new MessageRequest(['reaction'], this.groupId, newState, null, this.id);

        this.state.push(newState);
        this.callOnUpdate(['reaction']);

        // request.groupId = '123';

        Socket.instance.messageSocket.sendRequest(request).then((item) => {

          // @todo think something better
          // @todo idealy we need use group.processMessageRequestEvent
          LookinChatSDK.instance.groups.getById(this.groupId)?.callOnMessagesUpdate([item]);

          this.update(item.message, item.type);
          resolve()
        }).catch((error) => {
          LookinChatSDK.instance.groups.getById(this.groupId)?.callOnMessagesUpdate(error.result.items);
          this.update(error.result.items[0].message, error.result.items[0].type);

          LookinChatSDK.instance.callOnRejectUpdate(error.message);
          reject(error);
        });
      } else {
        resolve()
      }
    });
  }

  /**
   *  Delete reaction from message<br>
   *  State `reaction` will be removed immediately, and {@link onUpdate} will be called<br>
   *  After server will handle request, group will be updated again and {@link onUpdate} will be called again,<br>
   *  Also {@link Group.onMessagesUpdate} will be called<br>
   *  If message not ready, changes will be not made<br>
   *  If there is no reaction of current user, changes will be not made
   *
   * @return Promise, resolve if all ok ( or cant delete ), throw error in case of error.
   * */
  deleteReaction() {
    return new Promise<void>((resolve, reject) => {
      if (this.ready) {
        const existedSate = this.state.getByUserAndType('reaction', LookinChatSDK.user);

        if (existedSate) {
          this.state.removeByUserAndType('reaction', LookinChatSDK.user);
          const request = new MessageRequest(['deleteReaction'], this.groupId, null, null, this.id);

          this.callOnUpdate(['deleteReaction']);

          // request.groupId = '123';

          Socket.instance.messageSocket.sendRequest(request).then((item) => {
            // @todo think something better
            LookinChatSDK.instance.groups.getById(this.groupId)?.callOnMessagesUpdate([item]);

            this.update(item.message, item.type);
            resolve()
          }).catch((error) => {
            LookinChatSDK.instance.groups.getById(this.groupId)?.callOnMessagesUpdate(error.result.items);
            this.update(error.result.items[0].message, error.result.items[0].type);

            reject(error);
          });
        } else {
          resolve()
        }
      } else {
        resolve()
      }
    });
  }

  /**
   *  Edit message<br>
   *  New state `edit` and content will be added immediately, and {@link onUpdate} will be called<br>
   *  After server will handle request, group will be updated again and {@link onUpdate} will be called again,<br>
   *  Also {@link Group.onMessagesUpdate} will be called<br>
   *  If message not ready, changes will be not made
   *
   *  @param content Partial of {@link MessageContent}
   *
   * @return Promise, resolve if all ok ( or cant edit ), throw error in case of error.
   * */
  edit(content: Partial<MessageContent>) {
    return new Promise<void>((resolve, reject) => {
      if (this.ready) {

        if (content.attachments?.length) {
          content.attachments = Helper.convertFiles(content.attachments);
        }

        const newState = new MessageState('edit')
        const newContent = plainToClassFromExist(this.content, content);
        const request = new MessageRequest(['edit'], this.groupId, newState, newContent, this.id);

        this.content = newContent;
        this.state.push(newState);

        this.callOnUpdate(['edit']);

        // request.groupId = '123';

        Socket.instance.messageSocket.sendRequest(request).then((item) => {
          // @todo think something better
          LookinChatSDK.instance.groups.getById(this.groupId)?.callOnMessagesUpdate([item]);

          this.update(item.message, item.type)
          resolve()
        }).catch((error) => {
          LookinChatSDK.instance.groups.getById(this.groupId)?.callOnMessagesUpdate(error.result.items);
          this.update(error.result.items[0].message, error.result.items[0].type)

          LookinChatSDK.instance.callOnRejectUpdate(error.message);
          reject(error);
        });
      } else {
        resolve()
      }
    });
  }

  /**
   *  Try to send error message again<br>
   *
   * @return Promise, resolve if all ok ( or cant send again ), throw error in case of error.
   * */
  retry() {
    return new Promise<void>((resolve, reject) => {
      if (this.status === 'error') {
        const request = new MessageRequest(['create'], this.groupId, this.state[0], this.content)

        this.status = 'sending';
        this.callOnUpdate(['create']);

        // request.groupId = '123';

        Socket.instance.messageSocket.sendRequest(request).then((item) => {
          // @todo think something better
          LookinChatSDK.instance.groups.getById(this.groupId)?.callOnMessagesUpdate([item]);

          this.update(item.message, item.type)
          resolve()
        }).catch((error) => {
          this.status = 'error';
          this.callOnUpdate(['create']);

          LookinChatSDK.instance.callOnRejectUpdate(error.message);
          reject(error);
        });
      } else {
        resolve()
      }
    });
  }

  /**
   *  Delete message<br>
   *  New state `delete` will be added immediately<br>
   *  Status will be changed to `deleted`, type ot `infoMessage` and content emptied<br>
   *  {@link onUpdate} will be called<br>
   *  After server will handle request, group will be updated again and {@link onUpdate} will be called again,<br>
   *  Also {@link Group.onMessagesUpdate} will be called<br>
   *  If message not ready, changes will be not made<br>
   *  If existed reaction from this user will be sent, {@link deleteReaction} will be called instead<br>
   *  If user already reacted, and send another reaction, then it will be replaced with new one
   *
   * @return Promise, resolve if all ok ( or cant delete ), throw error in case of error.
   * */
  delete() {
    return new Promise<void>((resolve, reject) => {
      if (this.ready) {
        const newState = new MessageState('delete')
        const newContent = new MessageContent()
        const request = new MessageRequest(['delete'], this.groupId, newState, newContent, this.id);

        this.status = 'deleted';
        this.type = 'infoMessage';

        this.content = newContent;
        this.state.push(newState);

        this.callOnUpdate(['delete']);

        // request.groupId = '123';

        Socket.instance.messageSocket.sendRequest(request).then((item) => {
          LookinChatSDK.instance.groups.getById(this.groupId)?.callOnMessagesUpdate([item]);

          this.update(item.message, item.type)
          resolve()
        }).catch((error) => {
          LookinChatSDK.instance.groups.getById(this.groupId)?.callOnMessagesUpdate(error.result.items);
          this.update(error.result.items[0].message, error.result.items[0].type)

          LookinChatSDK.instance.callOnRejectUpdate(error.message);
          reject(error);
        });
      } else if (this.status === 'error') {
        const group = LookinChatSDK.instance.groups.getById(this.groupId);
        group?.messages.removeByField('clientId', this.clientId);
        group?.callOnMessagesUpdate();

        resolve()
      } else {
        resolve()
      }
    });
  }

  /**
   * Will register anonymous function to change message entity event.
   *
   * @param callback - Anonymous function that will be called each time when message entity will be changed.
   * */
  registerOnUpdate(callback: (type: MessageStateType[]) => void) {
    this.onUpdate = callback;
  }
}

/**
 * Message type<br>
 * `userMessage` - if message was sent by user<br>
 * `infoMessage` - if it is info ( system ) message
 * */
export type MessageType = 'userMessage' | 'infoMessage';

/**
 * Message status<br>
 * `sending` - message was created and sent to server,<br>
 * `sent` - message was saved on server,<br>
 * `deleted` - message was deleted, in this case {@link type} will be changed to `infoMessage` and {@link content} also changed<br>
 * `error` - message was not sent for some reason.
 * */
export type MessageStatus = 'sending' | 'sent' | 'deleted' | 'error';
